import { CalendarRange, Loader2 } from "lucide-react";

import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { useTenantStore } from "@/stores/useTenantStore";
import { useLedger } from "@/hooks/usePlanner";

function monthLabel(period: string): string {
  const [y, m] = period.split("-").map(Number);
  if (!y || !m) return period;
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

function availableTone(v: number): string {
  if (v < 0) return "text-destructive";
  if (v === 0) return "text-muted-foreground";
  return "text-teal-dark";
}

export default function PlannerClientSummaryPage() {
  const { currentTenant } = useTenantStore();
  const { data: ledger = [], isLoading } = useLedger(currentTenant);

  const now = new Date();
  const nowPeriod = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center gap-3">
        <CalendarRange className="h-6 w-6 text-coral" />
        <div>
          <h1 className="text-xl font-bold tracking-tight text-foreground">Client Summary</h1>
          <p className="text-sm text-muted-foreground">
            {currentTenant || "Select a client"} — monthly points ledger with rolling carryover.
          </p>
        </div>
      </div>

      <Card className="border-0 shadow-sm">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/20">
                <TableHead className="min-w-[180px]">Month</TableHead>
                <TableHead className="text-right">Budget</TableHead>
                <TableHead className="text-right">Used</TableHead>
                <TableHead className="text-right">Carryover</TableHead>
                <TableHead className="text-right">Available</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={5} className="py-10 text-center text-muted-foreground">
                    <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading ledger…
                  </TableCell>
                </TableRow>
              ) : ledger.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="py-10 text-center text-sm text-muted-foreground">
                    No plan history yet — set a budget or add tasks on the Agile Plan.
                  </TableCell>
                </TableRow>
              ) : (
                ledger.map((row) => {
                  const active = row.billing_period === nowPeriod;
                  return (
                    <TableRow key={row.billing_period} className={cn(active && "bg-coral-light/50 font-medium")}>
                      <TableCell>
                        {monthLabel(row.billing_period)}
                        {active ? (
                          <span className="ml-2 rounded-full bg-coral px-1.5 py-0.5 text-[10px] font-semibold text-white">Active</span>
                        ) : null}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{row.budget}</TableCell>
                      <TableCell className="text-right tabular-nums">{row.used}</TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">{row.carryover}</TableCell>
                      <TableCell className={cn("text-right font-semibold tabular-nums", availableTone(row.available))}>
                        {row.available}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
